import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

type Member = Tables<"users">;
type MemberInsert = InsertTables<"users">;
type MemberUpdate = UpdateTables<"users">;

async function fetchMembers(): Promise<Member[]> {
  const { data, error } = await supabase
    .from("users")
    .select("*")
    .order("full_name");

  if (error) throw error;
  return data || [];
}

async function fetchMemberById(id: string): Promise<Member | null> {
  const { data, error } = await supabase
    .from("users")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
}

async function createMember(member: MemberInsert): Promise<Member> {
  const { data, error } = await supabase
    .from("users")
    .insert(member)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateMember(id: string, member: MemberUpdate): Promise<Member> {
  const { data, error } = await supabase
    .from("users")
    .update(member)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteMember(id: string): Promise<void> {
  const { error } = await supabase.from("users").delete().eq("id", id);

  if (error) throw error;
}

export function useMembers() {
  return useQuery({
    queryKey: ["members"],
    queryFn: fetchMembers,
  });
}

export function useMember(id: string | undefined) {
  return useQuery({
    queryKey: ["members", id],
    queryFn: () => (id ? fetchMemberById(id) : null),
    enabled: !!id,
  });
}

export function useCreateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, member }: { id: string; member: MemberUpdate }) =>
      updateMember(id, member),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      queryClient.invalidateQueries({ queryKey: ["members", variables.id] });
    },
  });
}

export function useDeleteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
}
